import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  BarChart3,
  Building2,
  CalendarDays,
  CheckCircle2,
  CircleDashed,
  Clock3,
  Download,
  MapPin,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  UserRound,
  WalletCards,
} from "lucide-react";
import { publicAssetUrl } from "../../lib/adminApi";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

const fallbackProject = {
  id: 1,
  title: "Sample TETFund Intervention Project",
  slug: "sample-tetfund-intervention-project",
  project_type: "TETFund",
  funding_source: "TETFund",
  category: "Infrastructure",
  status: "ongoing",
  completion_percentage: 65,
  location: "Main Campus",
  description:
    "Sample institutional project record. Replace this content with official project details from the admin CMS.",
  impact:
    "This project supports institutional growth, learning infrastructure, improved service delivery and better student experience.",
  budget_amount: 450000000,
  contractor: "Not stated",
  start_date: "2026-01-15",
  expected_completion_date: "2026-12-15",
};

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0 },
};

function titleOf(item) {
  return item?.title || item?.name || "Institutional Project";
}

function textOf(item) {
  return item?.description || item?.summary || "Project record and progress update.";
}

function statusOf(item) {
  return String(item?.status || "planned").toLowerCase();
}

function progressOf(item) {
  return Math.max(
    0,
    Math.min(
      100,
      Number(item?.completion_percentage || item?.progress || item?.progress_percentage || 0)
    )
  );
}

function sourceOf(item) {
  return item?.funding_source || item?.project_type || item?.category || "Institutional";
}

function locationOf(item) {
  return item?.location || item?.site || item?.campus || "Federal Polytechnic Bida";
}

function contractorOf(item) {
  return item?.contractor || item?.contractor_name || "Not stated";
}

function money(value) {
  if (!value) return "Not stated";
  const number = Number(value);
  if (!Number.isFinite(number)) return value;
  return number.toLocaleString(undefined, {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
  });
}

function date(value) {
  if (!value) return "Not stated";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function statusMeta(status) {
  const s = String(status).toLowerCase();
  if (s === "completed") {
    return {
      label: "Completed",
      icon: CheckCircle2,
      badge: "bg-emerald-100 text-emerald-800",
      hero: "from-emerald-500 to-cyan-400",
    };
  }
  if (s === "ongoing") {
    return {
      label: "Ongoing",
      icon: TrendingUp,
      badge: "bg-blue-100 text-blue-800",
      hero: "from-blue-500 to-emerald-400",
    };
  }
  if (s === "suspended") {
    return {
      label: "Suspended",
      icon: CircleDashed,
      badge: "bg-rose-100 text-rose-800",
      hero: "from-rose-500 to-orange-400",
    };
  }
  return {
    label: "Planned",
    icon: Clock3,
    badge: "bg-amber-100 text-amber-800",
    hero: "from-amber-400 to-blue-500",
  };
}

function InfoCard({ icon: Icon, label, value }) {
  return (
    <motion.div
      variants={fadeUp}
      className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <div className="flex items-start gap-4">
        <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-blue-50 text-blue-700">
          <Icon className="h-5 w-5" />
        </span>
        <div>
          <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">
            {label}
          </p>
          <p className="mt-1 text-base font-black text-slate-950">{value}</p>
        </div>
      </div>
    </motion.div>
  );
}

export default function TetfundProjectDetailEnhanced() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      try {
        const [detailRes, listRes] = await Promise.all([
          fetch(`${API_BASE}/api/projects/${slug}`),
          fetch(`${API_BASE}/api/projects`),
        ]);

        const detail = await detailRes.json();
        const list = await listRes.json();

        if (!mounted) return;

        setProject(detail?.id ? detail : fallbackProject);
        setRelated(
          Array.isArray(list)
            ? list.filter((item) => String(item.slug || item.id) !== String(slug)).slice(0, 3)
            : []
        );
      } catch (error) {
        if (mounted) {
          setProject(fallbackProject);
          setRelated([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [slug]);

  const current = project || fallbackProject;
  const status = statusMeta(statusOf(current));
  const StatusIcon = status.icon;
  const progress = progressOf(current);
  const image = current.image_url ? publicAssetUrl(current.image_url) : "";

  const timeline = useMemo(
    () => [
      { label: "Project announced", value: date(current.start_date || current.created_at), done: true },
      { label: "Implementation progress", value: `${progress}% completion`, done: progress > 0 },
      { label: "Expected completion", value: date(current.expected_completion_date), done: progress >= 100 },
    ],
    [current, progress]
  );

  if (loading) {
    return (
      <main className="min-h-screen bg-slate-50 px-6 py-20">
        <div className="mx-auto max-w-6xl animate-pulse">
          <div className="h-72 rounded-[2rem] bg-slate-200" />
          <div className="mt-8 h-12 w-2/3 rounded bg-slate-200" />
          <div className="mt-4 h-5 w-1/2 rounded bg-slate-200" />
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-50 text-slate-950">
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(59,130,246,.24),transparent_32%),radial-gradient(circle_at_88%_18%,rgba(16,185,129,.22),transparent_33%),linear-gradient(135deg,#04101e,#06253a_45%,#054a3b)]" />
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.7) 1px, transparent 1px),linear-gradient(90deg, rgba(255,255,255,.7) 1px, transparent 1px)",
            backgroundSize: "44px 44px",
          }}
        />

        <div className="relative mx-auto max-w-7xl px-6 py-16 lg:px-8 lg:py-20">
          <Link
            to="/tetfund-projects"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-4 py-2 text-sm font-black text-white/85 backdrop-blur transition hover:bg-white/15"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to projects
          </Link>

          <div className="mt-10 grid gap-10 lg:grid-cols-[1.05fr_.95fr] lg:items-center">
            <motion.div variants={fadeUp} initial="hidden" animate="show">
              <p className="mb-4 flex items-center gap-2 text-xs font-black uppercase tracking-[0.38em] text-amber-300">
                <Sparkles className="h-4 w-4" /> Project Details
              </p>

              <div className="flex flex-wrap gap-2">
                <span className="rounded-full bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-wide text-white/80">
                  {sourceOf(current)}
                </span>
                <span className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-black uppercase tracking-wide ${status.badge}`}>
                  <StatusIcon className="h-4 w-4" />
                  {status.label}
                </span>
              </div>

              <h1 className="mt-5 max-w-4xl text-4xl font-black leading-[0.98] tracking-tight md:text-6xl">
                {titleOf(current)}
              </h1>

              <p className="mt-6 max-w-2xl text-lg leading-8 text-white/78">
                {textOf(current)}
              </p>

              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                  <p className="text-xs font-black uppercase tracking-[0.2em] text-white/55">
                    Progress
                  </p>
                  <p className="mt-2 text-3xl font-black">{progress}%</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                  <p className="text-xs font-black uppercase tracking-[0.2em] text-white/55">
                    Budget
                  </p>
                  <p className="mt-2 text-xl font-black">{money(current.budget_amount)}</p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur">
                  <p className="text-xs font-black uppercase tracking-[0.2em] text-white/55">
                    Location
                  </p>
                  <p className="mt-2 text-xl font-black">{locationOf(current)}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 24 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ delay: 0.12, duration: 0.55 }}
              className="overflow-hidden rounded-[2rem] border border-white/10 bg-white/10 p-4 shadow-2xl backdrop-blur-xl"
            >
              <div className="relative h-[390px] overflow-hidden rounded-[1.5rem] bg-gradient-to-br from-slate-900 via-blue-900 to-emerald-900">
                {image ? (
                  <img
                    src={image}
                    alt={titleOf(current)}
                    className="h-full w-full object-cover"
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="absolute inset-0 grid place-items-center text-center">
                    <div>
                      <Building2 className="mx-auto h-16 w-16 text-white/70" />
                      <p className="mt-4 text-2xl font-black">Project Image</p>
                      <p className="mt-2 text-sm text-white/60">Image currently unavailable</p>
                    </div>
                  </div>
                )}

                <div className="absolute inset-x-5 bottom-5 rounded-2xl bg-slate-950/70 p-5 backdrop-blur">
                  <div className="mb-2 flex items-center justify-between text-sm font-black">
                    <span>Completion status</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="h-3 overflow-hidden rounded-full bg-white/10">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 1.1, delay: 0.25 }}
                      className={`h-full rounded-full bg-gradient-to-r ${status.hero}`}
                    />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
          <div className="space-y-8">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              className="rounded-[2rem] border border-slate-200 bg-white p-7 shadow-sm md:p-9"
            >
              <p className="text-xs font-black uppercase tracking-[0.28em] text-emerald-700">
                Project Overview
              </p>
              <h2 className="mt-3 text-3xl font-black text-slate-950">About this project</h2>
              <div className="mt-5 whitespace-pre-line text-base leading-8 text-slate-700">
                {textOf(current)}
              </div>
            </motion.div>

            {current.impact && (
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                className="rounded-[2rem] border border-emerald-100 bg-emerald-50 p-7 shadow-sm md:p-9"
              >
                <p className="text-xs font-black uppercase tracking-[0.28em] text-emerald-700">
                  Expected Impact
                </p>
                <h2 className="mt-3 text-3xl font-black text-emerald-950">Institutional value</h2>
                <div className="mt-5 whitespace-pre-line text-base leading-8 text-emerald-900">
                  {current.impact}
                </div>
              </motion.div>
            )}

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              className="rounded-[2rem] border border-slate-200 bg-white p-7 shadow-sm md:p-9"
            >
              <p className="text-xs font-black uppercase tracking-[0.28em] text-blue-700">
                Delivery Timeline
              </p>
              <h2 className="mt-3 text-3xl font-black text-slate-950">Milestone snapshot</h2>

              <div className="mt-8 space-y-4">
                {timeline.map((item, index) => (
                  <motion.div
                    key={item.label}
                    initial={{ opacity: 0, x: -16 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.06 }}
                    className="flex gap-4 rounded-2xl bg-slate-50 p-5"
                  >
                    <span
                      className={`mt-1 grid h-10 w-10 shrink-0 place-items-center rounded-full ${
                        item.done ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"
                      }`}
                    >
                      {item.done ? <CheckCircle2 className="h-5 w-5" /> : <Clock3 className="h-5 w-5" />}
                    </span>
                    <div>
                      <p className="font-black text-slate-950">{item.label}</p>
                      <p className="mt-1 text-sm font-semibold text-slate-500">{item.value}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          <aside className="space-y-6">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm"
            >
              <p className="text-xs font-black uppercase tracking-[0.28em] text-blue-700">
                Project Facts
              </p>
              <div className="mt-6 grid gap-4">
                <InfoCard icon={WalletCards} label="Funding Source" value={sourceOf(current)} />
                <InfoCard icon={BarChart3} label="Budget" value={money(current.budget_amount)} />
                <InfoCard icon={UserRound} label="Contractor" value={contractorOf(current)} />
                <InfoCard icon={MapPin} label="Location" value={locationOf(current)} />
                <InfoCard icon={CalendarDays} label="Start Date" value={date(current.start_date)} />
                <InfoCard icon={Clock3} label="Expected Completion" value={date(current.expected_completion_date)} />
              </div>
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
              className="rounded-[2rem] bg-gradient-to-br from-blue-700 to-emerald-700 p-6 text-white shadow-xl"
            >
              <ShieldCheck className="h-9 w-9 text-white/85" />
              <h3 className="mt-4 text-2xl font-black">Transparency note</h3>
              <p className="mt-3 text-sm leading-7 text-white/85">
                This project page presents official progress details in a public-facing format to improve institutional transparency and stakeholder trust.
              </p>
              <Link
                to="/tetfund-projects"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 text-sm font-black text-slate-950"
              >
                View all projects <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>

            {related.length > 0 && (
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.2 }}
                className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm"
              >
                <p className="text-xs font-black uppercase tracking-[0.28em] text-slate-500">
                  Related Projects
                </p>
                <div className="mt-5 space-y-3">
                  {related.map((item) => (
                    <Link
                      key={item.id || item.slug}
                      to={`/tetfund-projects/${item.slug || item.id}`}
                      className="block rounded-2xl bg-slate-50 p-4 transition hover:bg-blue-50"
                    >
                      <p className="line-clamp-2 font-black text-slate-950">{titleOf(item)}</p>
                      <p className="mt-1 text-sm text-slate-500">{progressOf(item)}% complete</p>
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}
          </aside>
        </div>
      </section>
    </main>
  );
}
